import React from 'react';
import { Map, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Leaflet resolves its default marker images relative to the CSS, which webpack
// rewrites — so without this the pin is an invisible broken image. Every other
// map in this app does the same; it is bundler plumbing, not a design choice.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

/**
 * A read-only map showing one point.
 *
 * For places a coordinate is REPORTED rather than chosen — a hazard's GPS, a
 * shared position — where the reader's question is "where is that?" and a pair
 * of decimals does not answer it. Deliberately not LocationPicker: nothing here
 * is editable, there is no property outline to fetch, and no state to own.
 *
 * The numbers stay on screen beside the map. They are what gets pasted into a
 * radio call or another system, and a map that replaced them would take away
 * the only part of this a person can carry somewhere else.
 *
 * Same Google hybrid tiles as the rest of the app — a plain Leaflet TileLayer,
 * so no API key is involved.
 */
const MapPointPreview = ({ lat, lng, height = 220, zoom = 16, label = null }) => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  const usable =
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180;

  // Out of range is not drawn at all. A pin in the wrong hemisphere reads as a
  // fact; nothing reads as nothing.
  if (!usable) {
    return null;
  }

  const position = [latitude, longitude];
  const text = `${latitude}, ${longitude}`;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 6,
        }}
      >
        {label ? <span style={{ fontWeight: 500 }}>{label}</span> : null}
        <span style={{ fontFamily: 'monospace' }}>{text}</span>
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 12 }}
        >
          Open in Google Maps
        </a>
      </div>

      <div
        style={{
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid #f0f0f0',
          maxWidth: 560,
        }}
      >
        <Map
          center={position}
          zoom={zoom}
          // Read-only: the point is a record of where something was, not a
          // control. Scroll-wheel zoom is off so the page still scrolls when
          // the cursor crosses the map.
          dragging
          zoomControl
          scrollWheelZoom={false}
          doubleClickZoom={false}
          style={{ width: '100%', height }}
        >
          <TileLayer
            attribution="&copy; Google Maps"
            url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
          />
          <Marker position={position} />
        </Map>
      </div>
    </div>
  );
};

export default MapPointPreview;
